import { useEffect, useId, useRef, useState } from "react";
import { useFontScale } from "../hooks/useFontScale";
import {
	FONT_SCALE_LABELS,
	FONT_SCALE_LEVELS,
	type FontScaleLevel,
	isFontScaleLevel,
} from "../lib/fontScale";

function TextSizeIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
			<path
				d="M3.25 13.5 7.1 4.5h1.3L12.25 13.5M4.55 10.6h5.25"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12.6 8.25h2.65M13.95 6.9v2.7"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
		</svg>
	);
}

/**
 * Toolbar text-size control — icon opens a compact slider popup (−2…+2).
 * Visual language matches ThemeControl (editorial, thin outline, soft chrome).
 */
export function FontScaleControl() {
	const { level, setLevel } = useFontScale();
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const labelId = useId();
	const valueId = useId();
	const sizeLabel = FONT_SCALE_LABELS[level];

	useEffect(() => {
		if (!open) return;
		const onPointer = (e: MouseEvent) => {
			const t = e.target as Node | null;
			if (t && rootRef.current?.contains(t)) return;
			setOpen(false);
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				setOpen(false);
				triggerRef.current?.focus();
			}
		};
		document.addEventListener("mousedown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const panel = panelRef.current;
		if (!panel) return;
		const focusables = panel.querySelectorAll<HTMLElement>(
			'input, button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		first?.focus();
		const onTab = (e: KeyboardEvent) => {
			if (e.key !== "Tab" || focusables.length === 0) return;
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last?.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first?.focus();
			}
		};
		panel.addEventListener("keydown", onTab);
		return () => panel.removeEventListener("keydown", onTab);
	}, [open]);

	return (
		<div className="group relative" ref={rootRef}>
			<button
				ref={triggerRef}
				type="button"
				aria-label={`Text size: ${sizeLabel}. Open size control`}
				aria-expanded={open}
				aria-haspopup="dialog"
				onClick={() => setOpen((v) => !v)}
				className={[
					"relative inline-flex size-9 items-center justify-center rounded-[10px] text-wi-text transition-colors duration-150 ease-in-out",
					"bg-wi-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/45",
					"hover:bg-wi-gold-soft/60",
					open ? "bg-wi-gold-soft/60" : "",
				].join(" ")}
			>
				<TextSizeIcon />
			</button>
			{!open ? (
				<span
					role="tooltip"
					className={[
						"pointer-events-none absolute top-full left-1/2 z-20 mt-1.5 -translate-x-1/2",
						"whitespace-nowrap rounded-full border border-wi-text/10 bg-wi-surface px-2.5 py-1",
						"font-medium text-[11px] text-wi-text shadow-none",
						"opacity-0 transition-opacity duration-100 ease-in-out",
						"group-hover:opacity-100 group-focus-within:opacity-100",
						"motion-reduce:transition-none",
					].join(" ")}
				>
					Text size
				</span>
			) : null}
			{open ? (
				<div
					ref={panelRef}
					role="dialog"
					aria-modal="false"
					aria-labelledby={labelId}
					className={[
						"absolute top-full right-0 z-30 mt-2 w-[min(16.5rem,calc(100vw-2rem))]",
						"rounded-xl border border-wi-text/10 bg-wi-surface p-3.5 shadow-none",
						"motion-reduce:transition-none",
					].join(" ")}
				>
					<div className="flex items-baseline justify-between gap-2">
						<p id={labelId} className="font-medium text-[12px] text-wi-text tracking-wide">
							Text size
						</p>
						<p id={valueId} className="text-[11px] text-wi-text/55" aria-live="polite">
							{sizeLabel}
						</p>
					</div>
					<label className="mt-3 block">
						<span className="sr-only">Adjust text size</span>
						<input
							type="range"
							min={-2}
							max={2}
							step={1}
							value={level}
							aria-valuemin={-2}
							aria-valuemax={2}
							aria-valuenow={level}
							aria-valuetext={sizeLabel}
							aria-describedby={valueId}
							onChange={(e) => {
								const next = Number.parseInt(e.target.value, 10);
								if (isFontScaleLevel(next)) setLevel(next);
							}}
							className="h-2 w-full cursor-pointer appearance-none rounded-full bg-wi-ink-soft accent-wi-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/45"
						/>
					</label>
					<div className="mt-1.5 flex justify-between text-[10px] text-wi-text/40" aria-hidden="true">
						{FONT_SCALE_LEVELS.map((step: FontScaleLevel) => (
							<span key={step}>{step === 0 ? "Aa" : step > 0 ? "+" : "−"}</span>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
}
